import Router from 'next/router';
import { useReduxState } from 'hooks';
import { CategoryBadges, LinkButton, Owners, User } from 'components/common';
import { PathPicture, PathsTileHeader, PathsTileSteps } from '.';

import type {
  Path,
  PathStep,
  PathStepProgress,
  ResolvedItemInPathStep,
  User as IUser,
  CompetencyInProduct,
  UserWithBasicData
} from 'shared-types';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';

type PathAttributes = Pick<
  Path,
  | 'id'
  | 'title'
  | 'description'
  | 'endDate'
  | 'picture'
  | 'picturePosition'
  | 'picturePosition'
  | 'startDate'
>;

export type PathInAdminTile = PathAttributes & {
  categories: string[];
  competencies: CompetencyInProduct[];
  deadlines?: { [stepId: string]: string };
  owners: UserWithBasicData[];
  progressData?: PathStepProgress;
  steps: {
    id: PathStep['id'];
    isMandatory: PathStep['isMandatory'];
    items: ResolvedItemInPathStep[];
    number: PathStep['number'];
  }[];
  userIsEnrolled: boolean;
};

interface Props {
  path: PathInAdminTile;
}

const PathsAdminTile = ({ path }: Props) => {
  const { messages, theme } = useReduxState();

  const handleClick = () => {
    Router.push(`/admin/paths/${path.id}`);
  };

  const linkProps = {
    category: 'lighter' as ButtonCategory,
    isSemiRounded: true,
    onClick: (ev) => ev.stopPropagation(),
    size: 'small' as ButtonSize,
    style: {
      flexShrink: 0,
      fontSize: '1.3rem',
      width: 30,
      height: 30
    }
  };

  return (
    <div className="path" onClick={handleClick}>
      <PathPicture
        picture={path.picture}
        picturePosition={path.picturePosition}
        size={960}
        style={{
          borderRadius: `${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`,
          height: 140
        }}
      />

      <div className="content">
        <PathsTileHeader
          numberOfSteps={path.steps?.length || 0}
          path={path}
          isPathTypeAdmin={true}
        />

        <div className="owners">
          {path.owners?.length ? (
            <>
              <User
                showNameOrUsername={false}
                size="tiny"
                user={path.owners?.[0]}
              />

              <Owners owners={path.owners} />
            </>
          ) : (
            messages.commonNoOwners
          )}
        </div>

        <CategoryBadges categories={path.categories} />

        <div className="footer">
          <div className="actions">
            <LinkButton
              href={`/admin/paths/${path.id}`}
              icon="stream"
              tooltip={messages.pathsSteps}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/paths/${path.id}/settings`}
              icon="cog"
              tooltip={messages.commonSettings}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/paths/${path.id}/publish`}
              icon="check-circle"
              tooltip={messages.commonPublish}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/paths/${path.id}/share`}
              icon="user"
              tooltip={messages.commonShare}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/paths/${path.id}/users`}
              icon="chart-line"
              tooltip={messages.commonFollow}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/paths/${path.id}/events`}
              icon="list"
              tooltip={messages.commonActivityLog}
              {...linkProps}
            />
            <LinkButton
              href={`/admin/paths/${path.id}/manage`}
              icon="tools"
              tooltip={messages.pagesManage}
              {...linkProps}
            />
          </div>
        </div>
      </div>

      {path.steps?.length ? (
        <div className="steps">
          <PathsTileSteps path={path} pathType="admin" />
        </div>
      ) : null}

      <style jsx>{`
        .path {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.3s;
        }
        .path:focus,
        .path:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .content {
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          min-height: 180px;
          padding: 15px;
        }
        .owners {
          align-items: center;
          color: ${theme.textColorLight};
          display: inline-flex;
          flex-shrink: 0;
          font-size: 1.3rem;
          font-weight: 600;
          gap: 5px;
          margin-top: 10px;
        }
        .footer {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: auto;
          padding-top: 20px;
        }
        .actions {
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          flex-wrap: wrap;
          gap: 10px;
        }
        .steps {
          border-top: 1px solid ${theme.lightGreyDarkActive};
          border-radius: 0 0 ${theme.cardBorderRadius};
          flex-grow: 1;
          overflow-y: scroll;
        }
      `}</style>
    </div>
  );
};

export default PathsAdminTile;
